import { logger } from '../shared/logger';
import {
    Callback,
    DEFAULT_APP_ID,
    EmbedToLoader,
    LoaderToEmbed,
    Params,
    Result,
    ResultCode,
    isEnvelope,
    validateParams,
    wrap
} from '../shared/protocol';

const DEFAULT_EMBED_PATH = '/palm_h5/embed/';

// 私有化部署：embed 与 loader 同镜像、同域名、不同 path，
// 客户域名只有部署时才知道。所以从加载 loader 的 <script> 自身 URL 推断 origin，
// 这样客户接入时零配置：<script src="https://customer.com/palm_h5/loader/palm-mobile-manager.js">
// 即可，无需再传任何 origin 参数。
//
// 对应方向：embed → loader 用 document.referrer 推断（见 src/embed/main.ts）。
function inferEmbedOrigin(): string {
  const currentScript =
    typeof document !== 'undefined'
      ? (document.currentScript as HTMLScriptElement | null)
      : null;
  if (currentScript?.src) {
    try {
      return new URL(currentScript.src).origin;
    } catch {
      // 落到下面的兜底
    }
  }
  return typeof window !== 'undefined' ? window.location.origin : '';
}

// 模块加载时立刻抓取 currentScript：等到 start() 被调用时 currentScript 已是 null。
const INFERRED_EMBED_ORIGIN = inferEmbedOrigin();

/**
 * Web 侧 SDK 入口，通过 iframe 隔离摄像头 & wasm。
 *
 * ```ts
 * const result = await PalmMobileManager.start({
 *   token, userId, userName, phoneNo,
 *   mode: Mode.Registration,
 * });
 * ```
 */
export class PalmMobileManager {
  private static running: Session | null = null;

  /** 当前 SDK 版本号（由构建注入，占位值便于调试） */
  static readonly version = '0.0.1';

  /**
   * 触发一次采集流程。
   *
   * @param params   业务参数，见 {@link Params}
   * @param callback 可选回调；未传时返回的 Promise 同样会 resolve 出同一份 Result
   */
  static start(
    params: Params,
    callback?: Callback,
  ): Promise<Result> {
    const deliver = (result: Result): Result => {
      try {
        callback?.(result);
      } catch (e) {
        logger.error('loader', 'callback threw', { error: e });
      }
      return result;
    };


    const validationError = validateParams(params);
    if (validationError) {
      logger.warn('loader', 'invalid params', { reason: validationError });
      return Promise.resolve(
        deliver({
          code: ResultCode.ErrInvalidParams,
          message: `Invalid parameters: ${validationError}`,
        }),
      );
    }

    if (PalmMobileManager.running) {
      logger.warn('loader', 'another session in progress, rejecting start');
      return Promise.resolve(
        deliver({
          code: ResultCode.ErrSdkRuntime,
          message: 'another session is in progress',
        }),
      );
    }

    const normalized: Params = {
      ...params,
      appId: params.appId ?? DEFAULT_APP_ID,
      enableManager: params.enableManager ?? true,
      customHeaders: params.customHeaders ?? {},
    };

    // 一次 start 一个新 sessionId，embed 收到 start 时也会用同一个 id，便于串联
    const sessionId = logger.newSession();
    logger.info('loader', 'start', {
      mode: normalized.mode,
      userId: normalized.userId,
      appId: normalized.appId,
      sessionId,
    });

    const session = new Session(normalized, sessionId);
    PalmMobileManager.running = session;
    return session.run().then(deliver).finally(() => {
      PalmMobileManager.running = null;
    });
  }

  /** 等价于用户点击左上角返回按钮；无进行中会话时 no-op。 */
  static back(): void {
    PalmMobileManager.running?.postBack();
  }
}

/** 单次采集会话：创建 iframe、握手、转发 params、回收资源 */
class Session {
  private readonly params: Params;
  private readonly embedOrigin = INFERRED_EMBED_ORIGIN;
  private readonly sessionId: string;
  private iframe: HTMLIFrameElement | null = null;
  // 在 iframe HTML/JS/CSS 下载与 Vue 挂载完成之前，宿主页只能看到 iframe 的背景色，
  // 直接放黑底会有明显黑屏感；这里叠一层同尺寸的 loading 占位，收到 embed 'ready' 后移除。
  private placeholder: HTMLDivElement | null = null;
  private popstateHandler: (() => void) | null = null;
  // teardown 主动调 history.back() 弹哨兵时置 true，用来让 popstateHandler 区分
  //   "用户手势后退" vs "自己触发的清栈 back"，避免在自清栈时又 push 新哨兵形成死循环。
  private tearingDown = false;

  constructor(params: Params, sessionId: string) {
    this.params = params;
    this.sessionId = sessionId;
  }

  run(): Promise<Result> {
    return new Promise((resolve) => {
      const onMessage = (evt: MessageEvent) => {
        // 严格校验来源，防止宿主页其它 iframe 伪造消息
        if (evt.origin !== this.embedOrigin) return;
        if (!isEnvelope<EmbedToLoader>(evt.data)) return;
        const msg = evt.data.msg;
        if (msg.type === 'ready') {
          logger.debug('loader', 'embed ready, posting start');
          this.dismissPlaceholder();
          this.post({ type: 'start', payload: this.params, sessionId: this.sessionId });
          return;
        }
        if (msg.type === 'result') {
          logger.info('loader', 'session finished', { code: msg.payload.code });
          window.removeEventListener('message', onMessage);
          this.teardown();
          resolve(msg.payload);
        }
      };
      window.addEventListener('message', onMessage);
      this.mount();
    });
  }

  private mount() {
    const container = document.body;

    // placeholder 必须先于 iframe 进入合成树，且 iframe 在 'ready' 之前 visibility:hidden，
    // 否则深色模式下会闪一帧跨域 iframe 的默认黑色 canvas。
    this.placeholder = createPlaceholder();
    container.appendChild(this.placeholder);

    const iframe = document.createElement('iframe');
    iframe.src = this.embedOrigin + DEFAULT_EMBED_PATH;
    iframe.allow = 'camera; microphone';
    Object.assign(iframe.style, {
      position: 'fixed',
      inset: '0',
      width: '100%',
      height: '100%',
      border: '0',
      zIndex: '2147483646',
      background: '#fff',
      visibility: 'hidden',
    } satisfies Partial<CSSStyleDeclaration>);
    container.appendChild(iframe);
    this.iframe = iframe;

    this.installBackSentinel();
  }

  // 宿主浏览器返回键 / 手势 → 转成 SDK 内部 back。
  // 打开 iframe 时压入一条带标识的 history 条目：popstate 触发即代表"用户想退出这个模态"，
  // 但 SDK 内部可能还有页栈（例如 /result → /my），因此不直接关，而是走同一个 back 通道，
  // 交由 embed 侧决定是 router.back 还是 finish。若 SDK 未关，需要再补一条哨兵以承接下次返回。
  //
  // handler 摘除时机很关键：不能在 teardown 触发 history.back() 前摘。因为 history.back()
  // 是异步的，若先摘再 back，浏览器完成弹栈时无人处理 popstate，栈已经 -1 但从外部看不出；
  // 一旦此刻用户手势后退赶到，浏览器会再弹一次，用户视觉上就是"第一次没反应，第二次过头到
  // 更早的历史条目"。因此保留 handler 到 back 完成，用 tearingDown 标志区分自触发与用户触发。
  //
  // 不做"当前还在哨兵上就 return"的早退：iframe 加载会往联合会话历史里塞一条 doc-load 条目
  // （克隆了哨兵的 history.state），首次侧滑先弹这条、落在哨兵上。若早退，首次侧滑被吞 →
  // 用户再滑一次时往往已到根路由 → performBack 直接 cancel，表现为"一滑就退出 SDK"。
  // 去掉早退后：任何非 teardown 的 popstate 都补哨兵 + 转发 back，首次侧滑即触发 router.back。
  private installBackSentinel() {
    history.pushState({ __palmSdk: this.sessionId }, '');
    this.popstateHandler = () => {
      if (this.tearingDown) {
        // teardown 自触发的 back 已完成：现在才是安全摘监听的时刻
        if (this.popstateHandler) {
          window.removeEventListener('popstate', this.popstateHandler);
          this.popstateHandler = null;
        }
        return;
      }
      history.pushState({ __palmSdk: this.sessionId }, '');
      this.postBack();
    };
    window.addEventListener('popstate', this.popstateHandler);
  }

  private dismissPlaceholder() {
    if (this.iframe) this.iframe.style.visibility = 'visible';
    if (!this.placeholder) return;
    const el = this.placeholder;
    this.placeholder = null;
    el.style.opacity = '0';
    // 与 transition 时长保持一致；即便用户切走了 tab 计时器被冻结，最坏也只是占位多停一会儿
    window.setTimeout(() => el.remove(), 200);
  }

  private teardown() {
    this.iframe?.remove();
    this.iframe = null;
    this.placeholder?.remove();
    this.placeholder = null;

    // 哨兵仍在栈顶 → 由 popstateHandler 在 back 完成后摘监听（tearingDown 分支）。
    // 哨兵已被用户手势 popstate 消费 → 此时监听已无用途，直接摘。
    if ((history.state as { __palmSdk?: string } | null)?.__palmSdk === this.sessionId) {
      this.tearingDown = true;
      history.back();
    } else if (this.popstateHandler) {
      window.removeEventListener('popstate', this.popstateHandler);
      this.popstateHandler = null;
    }
  }

  private post(msg: LoaderToEmbed) {
    this.iframe?.contentWindow?.postMessage(wrap(msg), this.embedOrigin);
  }

  postBack() {
    this.post({ type: 'back' });
  }
}

// 全屏 loading 占位：纯 inline 样式 + CSS keyframes 动画，避免依赖任何样式表。
// 与 iframe 同尺寸、z-index 比 iframe 高 1，'ready' 后淡出移除。
function createPlaceholder(): HTMLDivElement {
  const root = document.createElement('div');
  Object.assign(root.style, {
    position: 'fixed',
    inset: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    zIndex: '2147483647',
    transition: 'opacity 200ms ease-out',
    opacity: '1',
    pointerEvents: 'none',
  } satisfies Partial<CSSStyleDeclaration>);

  const SPIN_KEYFRAMES_ID = '__palm_mm_spin_keyframes__';
  if (!document.getElementById(SPIN_KEYFRAMES_ID)) {
    const style = document.createElement('style');
    style.id = SPIN_KEYFRAMES_ID;
    style.textContent =
      '@keyframes __palm_mm_spin__ { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }

  const spinner = document.createElement('div');
  Object.assign(spinner.style, {
    width: '36px',
    height: '36px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#1677ff',
    borderRadius: '50%',
    animation: '__palm_mm_spin__ 0.8s linear infinite',
  } satisfies Partial<CSSStyleDeclaration>);
  root.appendChild(spinner);

  return root;
}
